import { BaseInputFilter } from "domain/models/base-input-filter";
import {
  useCreateDocumentMutation,
  useDeleteDocumentMutation,
} from "presentation/hooks/use-query-document";
import {
  useCreateEmployeeMutation,
  useEmployeesById,
  useUpdateEmployeeMutation,
} from "presentation/hooks/use-query-employees";
import {
  useCreatesalaryMutation,
  useUpdatesalaryMutation,
  useSalariesByFilter,
} from "presentation/hooks/use-query-salary";
import { useJobTitleByFilter } from "presentation/hooks/use-query-job-title";
import { useParameterByFilter } from "presentation/hooks/use-query-parameter";
import { useDocumentDeleteStore } from "presentation/stores/use-document-delete-store";
import { useDocumentUploadStore } from "presentation/stores/use-document-upload-store";
import { useEmployeeStore } from "presentation/stores/use-employee-store";
import { useJobTitleStore } from "presentation/stores/use-job-title-store";
import { useParameterStore } from "presentation/stores/use-parameter-store";
import { Button } from "primereact/button";
import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from "primereact/toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Document } from "./../../../../domain/entities/document";
import "./index.css";
import PersonalInfoForm from "./PersonalInfoForm";
import ProfessionalInfoForm from "./ProfessionalInfoForm";
import { useCreateUserMutation } from "presentation/hooks/use-query-user";
import { User } from "domain/entities/user";
import {
  translateRegexMessage,
  translateText,
} from "infra/translation/translate";
import {
  emptyFieldsError,
  emptyFieldsError2,
  emptyFieldsError3,
} from "infra/translation/dictionary";
import {
  EmployeeField,
  useEmployeeFieldsStore,
} from "presentation/stores/use-employee-fields-store";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { EMAIL } from "domain/enum/email";
import { useCreateVersioningMutation } from "presentation/hooks/use-query-versioning";
import { Versioning } from "domain/entities/versioning";
import { Employee } from "domain/entities/employee";
import Spinner from "presentation/components/Shared/Spinner";
import {
  useProjectMembersByFilter,
  useUpdateProjectMemberMutation,
} from "presentation/hooks/use-query-project-member";
import { ProjectMemberInputFilter } from "domain/models/project-member-input-filter";
import { ProjectMember } from "domain/entities/project-member";
import DocumentGrid from "./DocumentGrid";
import SalaryForm from "./SalaryForm";
import { Salary } from "domain/entities/salary";
import { useRoleByFilter } from "presentation/hooks/use-query-role";
import { RoleInputFilter } from "domain/models/role-input-filter";
import EmployeeEquipmentsTab from "./EmployeeEquipmentsTab";

const checkEmployeeChanges = (
  previous?: Employee,
  current?: Employee,
): boolean => {
  if (!previous || !current) return true;

  const fieldsToIgnore = [
    "modifiedAt",
    "lastModifiedBy",
    "salary",
    "createdAt",
  ];

  const cleanObject = (obj: any) => {
    const cleaned = { ...obj };
    fieldsToIgnore.forEach((field) => delete cleaned[field]);

    // Normalizar managerAssociated para comparação
    if (
      cleaned.managerAssociated?.id === "none" ||
      !cleaned.managerAssociated?.id
    ) {
      cleaned.managerAssociated = null;
    }

    // Normalizar datas para comparação (converter para string ISO ou null)
    const dateFields = [
      "birthDate",
      "admissionDate",
      "terminationDate",
      "rfiExpiresAt",
    ];
    dateFields.forEach((field) => {
      if (cleaned[field]) {
        cleaned[field] = new Date(cleaned[field]).toISOString();
      } else {
        cleaned[field] = null;
      }
    });

    return cleaned;
  };

  const cleanedPrevious = cleanObject(previous);
  const cleanedCurrent = cleanObject(current);

  const previousJson = JSON.stringify(cleanedPrevious);
  const currentJson = JSON.stringify(cleanedCurrent);

  const hasChanges = previousJson !== currentJson;

  // Log para debug (pode ser removido em produção)
  if (!hasChanges) {
    console.log(
      " Nenhuma alteração detectada no employee - PUT não será chamado",
    );
  } else {
    console.log("Alterações detectadas no employee - PUT será chamado");
  }

  return hasChanges;
};

const rolesAllAllowedToSeeSalary = ["Admin", "Finanças"];

const EmployeeForm: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const { id } = useParams();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [previousEmployee, setPreviousEmployee] = useState<Employee>();
  const [employeeIdToAnalyse, setEmployeeIdToAnalyse] = useState<string>("");
  const [salaryDataReady, setSalaryDataReady] = useState(false);
  const [employeeJustSaved, setEmployeeJustSaved] = useState(false);
  const [isWaitingForSalary, setIsWaitingForSalary] = useState(false);
  const { employee, setEmployee, resetEmployee } = useEmployeeStore();
  const { employeeFields, setEmployeeFields } = useEmployeeFieldsStore();
  const { documentsUpload, setDocumentsUpload } = useDocumentUploadStore();
  const { documentsDelete, setDocumentsDelete } = useDocumentDeleteStore();
  const {
    email: userEmployeeEmail,
    userPermissions,
    userEmployee: userEmployee,
  } = useAuthStore();
  const {
    setContractTypes,
    setEmployeeStatus,
    setIdDocumentType,
    setWorkLocation,
    setWorkType,
    setMaritalStatus,
    setWorkCountry,
    setEmployeeCountry,
    setIsSuportService,
  } = useParameterStore();
  const { setJobTitle } = useJobTitleStore();
  const {
    mutate: createEmployeeMutation,
    error: creationError,
    isError: isCreationError,
    isSuccess: isCreationSuccess,
    data: employeeCreated,
    isPending: isCreateEmployee,
  } = useCreateEmployeeMutation();
  const {
    mutate: updateEmployeeMutation,
    error: updatingError,
    isError: isUpdatingError,
    isSuccess: isUpdatingSuccess,
    isPending: isUpdatingEmployee,
  } = useUpdateEmployeeMutation();
  const {
    mutate: createSalaryMutation,
    error: creationSalaryError,
    isError: isCreationSalaryError,
    isSuccess: isCreationSalarySuccess,
  } = useCreatesalaryMutation();
  const {
    mutate: updateSalaryMutation,
    error: updatingSalaryError,
    isError: isUpdatingSalaryError,
    isSuccess: isUpdatingSalarySuccess,
  } = useUpdatesalaryMutation();
  const { data: existingSalaryData, refetch: refetchExistingSalary } =
    useSalariesByFilter({
      employeeId: employeeIdToAnalyse,
    });
  const {
    mutate: createDocumentMutation,
    error: creationDocError,
    isError: isCreationDocError,
    isSuccess: isCreationDocSuccess,
    data: documentCreated,
    isPending: isCreateDocument,
  } = useCreateDocumentMutation();
  const {
    mutate: createUserMutation,
    error: creationUserError,
    isError: isCreationUseError,
    isSuccess: isCreationUseSuccess,
    data: userCreated,
  } = useCreateUserMutation();
  const {
    mutate: deleteDocumentMutation,
    error: deletingDocError,
    isError: isDeletingDocError,
    isSuccess: isDeletingDocSuccess,
  } = useDeleteDocumentMutation();
  const {
    data,
    isError,
    isLoading,
    error,
    refetch: refetechEmployee,
  } = useEmployeesById(id);
  const {
    mutate: createVersioningMutation,
    error: creationVersioningError,
    isError: isCreationVersioningError,
    isSuccess: isCreationVersioningSuccess,
    data: versioningCreated,
  } = useCreateVersioningMutation();

  const { data: projectMembersData, refetch: refetchProjectMember } =
    useProjectMembersByFilter({
      employeeId: employee?.id,
      limit: 100,
    } as ProjectMemberInputFilter);
  const {
    mutate: updateProjectMemberMutation,
    error: updatingProjMemberError,
    isError: isUpdatingProjMemberError,
    isSuccess: isUpdatingProjMemberSuccess,
  } = useUpdateProjectMemberMutation();

  const { data: parameters } = useParameterByFilter({
    limit: 1000,
    sortBy: "value",
  } as BaseInputFilter);
  const { data: jobTitles } = useJobTitleByFilter({
    limit: 100,
  } as BaseInputFilter);

  const { data: rolesData, isLoading: areRolesLoading } = useRoleByFilter({
    limit: 100,
  } as RoleInputFilter);
  const hasSalaryTabPermission = useMemo(() => {
    if (!rolesData?.data || !userPermissions) {
      return false;
    }
    const allowedRoleIds = rolesData.data
      .filter((role) => rolesAllAllowedToSeeSalary.includes(role.name))
      .map((role) => role.id);

    return userPermissions.some(
      (permission) =>
        allowedRoleIds.includes(permission.roleId) ||
        (permission.role?.name &&
          rolesAllAllowedToSeeSalary.includes(permission.role.name)),
    );
  }, [rolesData?.data, userPermissions]);

  const location = useLocation();
  const canEdit = location.state?.canEdit ?? false;

  const tabs = useMemo(() => {
    const list = [
      {
        id: "personal",
        header: translateText("Personal Informations", "pt"),
        leftIcon: "pi pi-user mr-2",
        content: <PersonalInfoForm canEdit={canEdit} />,
      },
      {
        id: "professional",
        header: translateText("Professional Informations", "pt"),
        leftIcon: "pi pi-briefcase mr-2",
        content: <ProfessionalInfoForm canEdit={canEdit} />,
      },
    ];

    if (hasSalaryTabPermission) {
      list.push({
        id: "salary",
        header: translateText("Salary Informations", "pt"),
        leftIcon: "pi pi-euro mr-2",
        content: <SalaryForm canEdit={canEdit} />,
      });
    }

    list.push({
      id: "equipments",
      header: translateText("Equipamentos e Viaturas", "pt"),
      leftIcon: "pi pi-box mr-2",
      content: <EmployeeEquipmentsTab employeeId={employee?.id} />,
    });

    list.push({
      id: "documents",
      header: translateText("Documents", "pt"),
      leftIcon: "pi pi-file mr-2",
      content: <DocumentGrid canEdit={canEdit} />,
    });

    return list;
  }, [canEdit, hasSalaryTabPermission, employee?.id]);

  const maxTabIndex = tabs.length - 1;
  const isLastTab = activeIndex >= maxTabIndex;
  const buttonLabel = isLastTab ? "Save" : "Next";

  useEffect(() => {
    setContractTypes(
      parameters?.data?.filter((item) => item.type === "contractType"),
    );

    setEmployeeStatus(
      parameters?.data?.filter((item) => item.type === "employeeStatus"),
    );

    setIsSuportService(
      parameters?.data?.filter((item) => item.type === "isSuportService"),
    );

    setIdDocumentType(
      parameters?.data?.filter((item) => item.type === "idDocumentType"),
    );

    setMaritalStatus(
      parameters?.data?.filter((item) => item.type === "maritalStatus"),
    );

    setWorkCountry(
      parameters?.data?.filter((item) => item.type === "workCountry"),
    );

    setWorkLocation(
      parameters?.data?.filter((item) => item.type === "workLocation"),
    );

    setEmployeeCountry(
      parameters?.data?.filter((item) => item.type === "employeeCountry"),
    );

    setWorkType(parameters?.data?.filter((item) => item.type === "workType"));
  }, [parameters]);

  useEffect(() => {
    setEmployeeFields([]);
    setDocumentsDelete([]);
    setDocumentsUpload([]);
  }, []);

  useEffect(() => {
    setPreviousEmployee(data?.data);
  }, [data]);

  useEffect(() => {
    return () => {
      resetEmployee();
    };
  }, []);

  useEffect(() => {
    setJobTitle(jobTitles?.data);
  }, [jobTitles]);

  useEffect(() => {
    if (data && data.data) {
      setEmployee(data.data);
    } else if (isError) {
      console.error("Erro ao carregar dados do colaborador:", error);
    }
  }, [data, isError, error]);

  useEffect(() => {
    if (employeeIdToAnalyse && existingSalaryData) {
      setSalaryDataReady(true);
    }
  }, [existingSalaryData]);

  useEffect(() => {
    if (employee) {
      setEmployeeIdToAnalyse(employee.id);
      refetchExistingSalary();
    }
  }, [employee?.id]);

  const handleNextTab = async () => {
    if (isLastTab && employee) {
      const validationErrors: EmployeeField[] = [];
      if (employee.hasDrivingLicence === true) {
        if (
          !employee.carRegistration ||
          employee.carRegistration.trim() === ""
        ) {
          validationErrors.push({ field: "carRegistration", invalid: true });
        }
        if (!employee.carOwner || employee.carOwner.trim() === "") {
          validationErrors.push({ field: "carOwner", invalid: true });
        }

        if (validationErrors.length > 0) {
          setEmployeeFields(validationErrors);
          setIsSaving(false);

          toast.current?.show({
            severity: "error",
            summary: "Erro de Validação",
            detail: "Por favor preencha todos os campos obrigatórios",
            life: 3000,
          });

          setActiveIndex(1);
          return;
        }
      }

      setIsSaving(true);
      employee.lastModifiedBy = userEmployeeEmail ?? EMAIL;

      if (employee.id) {
        const toVersion: Versioning = {
          type: "Employee",
          objectId: employee.id,
          data: JSON.stringify(previousEmployee),
        };
        createVersioningMutation(toVersion);
      }

      if (employee && !employee.managerAssociated) {
        employee.managerAssociated = {
          id: "none",
          name: "NENHUM",
        };
      }

      const employeePreUpdate = data?.data;
      if (employee.id && employeePreUpdate?.isActive && !employee.isActive) {
        projectMembersData?.data?.forEach((pm) => {
          const pmInactive: ProjectMember = {
            ...pm,
            status: "Inativo",
            endAt: new Date(),
          };
          updateProjectMemberMutation(pmInactive);
        });
      }

      employee.lastModifiedBy = userEmployeeEmail ?? EMAIL;

      const { salary: _, ...employeeWithoutSalary } = employee;
      const employeeToSave = {
        ...employeeWithoutSalary,
        salary: null,
      } as Employee;

      if (!employee.id) {
        createEmployeeMutation(employeeToSave);
      } else {
        // Comparar se houve alterações antes de fazer update
        const hasChanges = checkEmployeeChanges(
          previousEmployee,
          employeeToSave,
        );
        if (hasChanges) {
          updateEmployeeMutation(employeeToSave);
        } else {
          // Se não houver alterações no employee, prosseguir apenas com salário e documentos
          const employeeId = employee.id;
          saveDocuments(employeeId);
          saveSalary(employeeId);

          setIsSaving(false);
          setEmployeeFields([]);

          toast.current?.show({
            severity: "success",
            summary: "Salvo com Sucesso",
            detail:
              "Salário e documentos atualizados (sem alterações nos dados do colaborador)",
            life: 2000,
          });

          setTimeout(() => {
            resetEmployee();
            navigate("/employees");
          }, 2000);
        }
      }
    } else if (activeIndex < maxTabIndex) {
      setActiveIndex(activeIndex + 1);
    }
  };

  useEffect(() => {
    if (isCreationError || isUpdatingError) {
      setIsSaving(false);
      let message = [];
      message.push(
        isCreationError ? creationError.message : updatingError.message,
      );
      let errorsToConsider: string[] = [];

      if (
        creationError &&
        creationError.response.data.metadata.errors &&
        creationError.response.data.metadata.errors.length > 0
      ) {
        message = creationError.response.data.metadata.errors;
        errorsToConsider = creationError.response.data.metadata.fields;
      } else if (
        updatingError &&
        updatingError.response?.data?.metadata.errors &&
        updatingError.response?.data?.metadata.errors.length > 0
      ) {
        message = updatingError.response.data.metadata.errors;
        errorsToConsider = updatingError.response.data.metadata.fields;
      }
      // console.log("message", message);

      let emptyFields: EmployeeField[] = [];
      let wrongFields: EmployeeField[] = [];
      const otherMessages: string[] = [];

      message.forEach((item: string) => {
        if (
          item.match(emptyFieldsError) ||
          item.match(emptyFieldsError2) ||
          item.match(emptyFieldsError3)
        ) {
          const fields = item.split(":")[0];
          if (fields.includes(","))
            emptyFields.push({ field: fields.split(",")[0], invalid: true });
          else emptyFields.push({ field: fields, invalid: true });
        } else {
          otherMessages.push(item);
          const fieldV = item.split(":")[0];
          if (fieldV.includes(",")) {
            fieldV.split(",").forEach((fv: string) => {
              if (!(fv in wrongFields)) {
                wrongFields.push({ field: fv, invalid: true });
              }
            });
          } else if (!(fieldV in wrongFields)) {
            wrongFields.push({ field: fieldV, invalid: true });
          }
          errorsToConsider?.forEach((fieldError: string) => {
            if (!(fieldError in wrongFields)) {
              wrongFields.push({ field: fieldError, invalid: true });
            }
          });
        }
      });

      if (emptyFields.length > 0) {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: translateRegexMessage(
            "There are fields that must be filled in.",
            "pt",
          ),
          life: 1500,
        });
      }

      setEmployeeFields(emptyFields);

      let bigErrorMessage: string[] = [];

      otherMessages.forEach((item: string) => {
        const translated = translateRegexMessage(item, "pt");
        bigErrorMessage.push(`- ${translated}`);
      });

      if (bigErrorMessage.length > 0 && emptyFields.length === 0) {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: (
            <div>
              {bigErrorMessage.map((msg, i) => (
                <div key={i}>{msg}</div>
              ))}
            </div>
          ),
          life: 1500,
        });

        wrongFields.forEach((wf) => {
          emptyFields.push(wf);
        });
        setEmployeeFields(emptyFields);
      }
    }
  }, [
    creationError,
    updatingError,
    setEmployeeFields,
    isCreationError,
    isUpdatingError,
  ]);

  useEffect(() => {
    if (isCreationDocError || isDeletingDocError) {
      let message = isCreationError
        ? creationDocError.message
        : deletingDocError.message;

      if (
        creationDocError.response.data.metadata.errors &&
        creationDocError.response.data.metadata.errors.length > 0
      ) {
        message = creationDocError.response.data.metadata.errors;
      } else if (
        deletingDocError.response.data.metadata.errors &&
        deletingDocError.response.data.metadata.errors.length > 0
      ) {
        message = deletingDocError.response.data.metadata.errors;
      }

      message.map((item: string) => {
        item = translateRegexMessage(item, "pt");
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: item || "Ocorreu um erro inesperado",
          life: 1500,
        });
      });
    }
  }, [creationDocError, deletingDocError]);

  useEffect(() => {
    if (isCreationSalaryError || isUpdatingSalaryError) {
      setIsSaving(false);
      let message: string[] = [];
      const errorObj = isCreationSalaryError
        ? creationSalaryError
        : updatingSalaryError;

      if (errorObj?.response?.data?.metadata?.errors) {
        message = errorObj.response.data.metadata.errors;
      } else {
        message = [errorObj?.message || "Erro ao salvar salário"];
      }

      message.forEach((item: string) => {
        const translated = translateRegexMessage(item, "pt");
        toast.current?.show({
          severity: "error",
          summary: "Erro no Salário",
          detail: translated,
          life: 3000,
        });
      });
    }
  }, [
    isCreationSalaryError,
    isUpdatingSalaryError,
    creationSalaryError,
    updatingSalaryError,
  ]);

  useEffect(() => {
    if (isCreationSuccess || isUpdatingSuccess) {
      setEmployeeJustSaved(true);
    }
  }, [isCreationSuccess, isUpdatingSuccess]);

  useEffect(() => {
    const handleFinalSuccess = () => {
      let successMessage = isCreationSuccess
        ? "Employee successfully registered"
        : "Employee successfully updated";

      successMessage = translateRegexMessage(successMessage, "pt");

      refetechEmployee();
      setEmployeeFields([]);

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: successMessage,
        life: 1500,
      });

      setTimeout(() => {
        resetEmployee();
        navigate("/employees");
      }, 1500);
    };

    if (
      isWaitingForSalary &&
      (isCreationSalarySuccess || isUpdatingSalarySuccess)
    ) {
      handleFinalSuccess();
      setIsWaitingForSalary(false);
    }

    if (employeeJustSaved) {
      const employeeId = employee?.id || employeeCreated?.data?.id;
      if (!employeeId) return;

      if (isCreationSuccess && employee) {
        const user: User = {
          email: employee.emailAddress,
          profileType: "Employee",
          profileId: employeeId,
          roles: [],
        };
        (employee.contractType === "CTI" || employee.contractType === "CTP") &&
          user.roles.push({
            id: "e2ff4a91-d422-414b-a1be-09b6cac63a7c",
            name: "CTI",
          });
        (employee.contractType === "CPS Nacional" ||
          employee.contractType === "CPS Internacional") &&
          user.roles.push({
            id: "9acc03b3-fae8-4f9c-b715-43b1c32331ca",
            name: "CPS",
          });
        createUserMutation(user);
      }

      saveDocuments(employeeId);
      const salaryWillBeSaved = !!employee?.salary;
      if (salaryWillBeSaved) {
        saveSalary(employeeId);
        setIsWaitingForSalary(true);
      } else {
        handleFinalSuccess();
      }
      setEmployeeJustSaved(false);
    }
  }, [
    employeeJustSaved,
    isWaitingForSalary,
    isCreationSalarySuccess,
    isUpdatingSalarySuccess,
    resetEmployee,
    navigate,
    employeeCreated,
  ]);

  const saveDocuments = (employeeId: string) => {
    if (documentsUpload.length === 0) return;

    documentsDelete.forEach((item) => {
      if (item.url) {
        const currentDocument: Document = {
          id: item.id,
          documentType: {
            id: item.docTypeId,
          },
          employee: {
            id: employeeId,
          },
          fullPath: item.url,
          lastModifiedBy: userEmployeeEmail ?? EMAIL,
        };

        deleteDocumentMutation(currentDocument);
      }
    });
    setDocumentsDelete([]);

    documentsUpload.forEach((item) => {
      if (item.url) {
        const currentDocument: Document = {
          id: item.id,
          documentType: {
            id: item.docTypeId,
          },
          employee: {
            id: employeeId,
          },
          fullPath: item.url,
          lastModifiedBy: userEmployeeEmail ?? EMAIL,
        };

        if (currentDocument.id) deleteDocumentMutation(currentDocument);

        createDocumentMutation(currentDocument);
      }
    });

    setDocumentsUpload([]);
  };

  const saveSalary = (employeeId: string) => {
    const salaryToSave = employee?.salary;

    if (!salaryToSave || !employee) {
      console.log(" No salary or employee to save");
      return;
    }

    const existingSalary = existingSalaryData?.data?.at(0);

    const hasExistingSalary =
      existingSalary && existingSalary.id ? true : false;

    const salaryData: Salary = {
      ...salaryToSave,
    };

    if (hasExistingSalary) {
      salaryData.id = existingSalary?.id ?? "";
    }

    salaryData.employee = {
      id: employeeId,
      name: employee.name,
      emailAddress: employee.emailAddress,
      employeeNumber: employee.employeeNumber,
      managerAssociated: employee.managerAssociated,
      contractType: employee.contractType,
    };
    salaryData.employeeId = employeeId;

    if (salaryData.irsType && typeof salaryData.irsType === "object") {
      salaryData.irsType = (salaryData.irsType as any).value;
    }

    const hasDriverLicense = employee?.hasDrivingLicence === true;

    if (!hasDriverLicense) {
      salaryData.costsAllowanceValue = 0;
    }

    salaryData.lastModifiedBy = userEmployeeEmail ?? EMAIL;

    hasExistingSalary && hasSalaryTabPermission
      ? updateSalaryMutation(salaryData)
      : createSalaryMutation(salaryData);
  };

  const handleCancel = () => {
    resetEmployee();
    navigate("/employees");
  };

  if (areRolesLoading) {
    return <Spinner />;
  }

  return (
    <form className="card" autoComplete="off">
      {isSaving ||
      isCreateEmployee ||
      isUpdatingEmployee ||
      isCreateDocument ? (
        <div className="spinner-overlay">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="employee-form-tabs-container">
            <TabView
              activeIndex={activeIndex}
              onTabChange={(e) => setActiveIndex(e.index)}
            >
              {tabs.map((tab) => (
                <TabPanel
                  key={tab.id}
                  header={tab.header}
                  leftIcon={tab.leftIcon}
                >
                  {tab.content}
                </TabPanel>
              ))}
            </TabView>
          </div>
          <div className="employee-form-actions">
            <Button
              label="Cancelar"
              onClick={handleCancel}
              className="button-size"
              outlined
              style={{
                backgroundColor: "white",
                borderColor: "#2196f3",
                color: "#2196f3",
                borderRadius: "8px",
              }}
              type="button"
            />
            {(!isLastTab || (isLastTab && canEdit)) && (
              <Button
                type="button"
                label={translateText(buttonLabel, "pt")}
                onClick={handleNextTab}
                className="button-size"
              />
            )}
          </div>
        </>
      )}
      <Toast ref={toast} position="bottom-right" />
    </form>
  );
};
export default EmployeeForm;
